import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import Popup from './newPopup';

export interface IEditFieldPopupProps {
  title: string;
  buttonText?: string;
  descriptionText: string;
}

interface _IEditFieldPopupProps extends IEditFieldPopupProps {
  onSubmit?: () => void;
  onCloseCancel?: () => void;
  onChange?: (any) => void;
  cleanStart?: boolean;
  children?: React.ReactChild;
  initialValue?: string | number;
  inputName: string;
  inputError?: string;
  inputWarning?: string;
  innerInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  isButtonDisabled?: boolean;
  noFooterButtons?: boolean;
  className?: string;
  noLightBox?: boolean;
  dataTestId?: string;
}

// There is a refactor mission SBT-11611 to rewrite the component
const StyledPopup = styled(Popup)`
  ${({ theme }) => {
    return css`
      margin: 0 20px;
      padding: 40px 60px;
      max-width: 525px;
      min-width: 300px;
      width: 100%;

      @media only screen and ${theme.device.max.mobileXL} {
        padding: 40px 23px 20px;
      }

      .popup-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0;

        .description {
          margin-bottom: 32px;
          text-align: center;
          font-weight: 500;
          font-size: 18px;

          @media only screen and ${theme.device.max.mobileXL} {
            font-weight: 400;
            font-size: 14px;
            line-height: 21px;
            margin-bottom: 16px;
          }
        }
      }

      .buttons-row {
        margin-bottom: 0;
        margin-top: 45px;
        padding: 0;

        @media only screen and ${theme.device.max.mobileXL} {
          margin-top: 32px;
        }

        > .popup-confirm-button {
          width: 100%;
          max-width: 255px;
          margin-top: 0;
        }
      }

      .dialog-title {
        font-weight: 600;
        font-size: 25px;
        line-height: 30px;
        margin-bottom: 24px;
        padding: 0;

        @media only screen and ${theme.device.max.mobileXL} {
          font-weight: 700;
          font-size: 18px;
          line-height: 27px;
          margin-bottom: 16px;
        }
      }
    `;
  }}
`;

const EditFieldPopup: FC<_IEditFieldPopupProps> = ({
  onSubmit,
  onCloseCancel,
  onChange,
  buttonText,
  title,
  descriptionText,
  cleanStart,
  children,
  initialValue,
  inputName,
  inputError,
  innerInputRef,
  isButtonDisabled,
  noFooterButtons,
  className,
  noLightBox,
  dataTestId,
}) => {
  const [initialValueState] = React.useState(initialValue);

  React.useEffect(() => {
    // Clean the field for a fresh start
    if (cleanStart) {
      onChange({ target: { value: null, name: inputName } });
    }
    if (innerInputRef) {
      innerInputRef.current.focus();
    }
  }, []);

  const closeAndReset = React.useCallback(() => {
    !!onChange && onChange({ target: { value: initialValueState, name: inputName } });
    onCloseCancel();
  }, [initialValueState, inputName, onChange, onCloseCancel]);

  return (
    <StyledPopup
      {...{
        dataTestId,
        className,
        noFooterButtons,
        dataAt: 'input-edit-popup',
        onClose: closeAndReset,
        blueButtonAction: onSubmit,
        blueButtonText: buttonText,
        boldText: title,
        blueButtonClassName: 'blue-btn',
        isBlueButtonDisabled: !!inputError || isButtonDisabled,
        noLightBox,
      }}
    >
      <p className="description">{descriptionText}</p>
      {children}
    </StyledPopup>
  );
};

export default EditFieldPopup;
